import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// CONFIGS
import { EFeatureFlag } from '@app/feature-toggle-list';

// INTERFACES
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import {
  IInvoiceType,
  EInvoiceCreateTiming,
  EInvoiceCreateTimingDescription,
} from 'v2/modules/invoice/interfaces';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';

// SERVICES
import { trackByValue, trackByKey } from 'v2/modules/shared/util/track-by.util';

import { DialogConfig } from '../../../shared';
import { ManageApiService } from '../../../shared/services/api/manage-api.service';

// UTILS
import { UiConfirmationDialogComponent } from '../../../ui';
import { ChannelFacadeService } from '../../channel-facade.service';
import { CChannelTypesLookUp, EChannelType } from '../../contants/channel.constant';

import { IAppIntegrationAccount } from './../../../shared/interfaces/app-integration-account.interface';
import { IFieldLabel } from './../../../shared/interfaces/common.interface';
import {
  EChannelMetaKey,
  IChannel,
} from './../../../shared/interfaces/mtchannel.interface';
import { EnvironmentUtilService } from './../../../shared/services/util/environment-util.service';
import { FeatureFlagUtilService } from './../../../shared/services/util/feature-flag-util.service';
import { EAppAccountType } from './../../../system-setting/constants/app-integration-account.enum';

// CONSTANTS

@Component({
  selector: 'lum-channel-form-v2',
  templateUrl: './channel-form-v2.component.html',
  styleUrls: ['./channel-form-v2.component.scss'],
})
export class ChannelFormV2Component implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  selectedPullAppIntegrationAccount: Partial<IAppIntegrationAccount>;
  featureFlags: { [key: string]: boolean };
  environment = this.environmentUtilService.getEnvironment();
  selectedPushOrderAppIntegrationAccount: Partial<IAppIntegrationAccount>;
  isNonSalesChannel = true;
  isFulfillmentChannel = false;
  invoiceCreateTimings = [
    {
      key: EInvoiceCreateTiming.ORDER_RECEIVED,
      description: EInvoiceCreateTimingDescription.ORDER_RECEIVED,
    },
    {
      key: EInvoiceCreateTiming.SHIPMENT_CREATED,
      description: EInvoiceCreateTimingDescription.SHIPMENT_CREATED,
    },
    {
      key: EInvoiceCreateTiming.FULFILLMENT_ORDER_CREATED,
      description: EInvoiceCreateTimingDescription.FULFILLMENT_ORDER_CREATED,
    },
    {
      key: EInvoiceCreateTiming.FIRST_SHIPMENT_FULL_AMOUNT,
      description: EInvoiceCreateTimingDescription.FIRST_SHIPMENT_FULL_AMOUNT,
    },
  ];
  outgoingCustomFields: IFieldLabel[] = [];
  CChannelTypesLookUp = CChannelTypesLookUp;
  ftChannelTypeEnabled: boolean = false;
  shouldShowAutoPushOrderField: boolean = false;

  trackByValue = trackByValue;
  trackByKey = trackByKey;

  private onDestroy$ = new Subject();

  constructor(
    private fb: UntypedFormBuilder,
    private environmentUtilService: EnvironmentUtilService,
    private featureFlagUtilService: FeatureFlagUtilService,
    private route: ActivatedRoute,
    private channelFacadeService: ChannelFacadeService,
    private snackBar: MatSnackBar,
    private router: Router,
    private manageApiService: ManageApiService,
    private dialog: MatDialog,
    private featureToggleService: FeatureToggleService,
  ) {}

  ngOnInit(): void {
    this.channelFacadeService.updateState({ currentChannel: null });
    this.initFeatureFlags();
    this.initForm();
    this.initCurrentChannel();
    this.getOutgoingCustomFieldsMapping();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onAppAccountSelected(
    appIntegrationAccount: Partial<IAppIntegrationAccount>,
  ): void {
    this.selectedPullAppIntegrationAccount = appIntegrationAccount;
    setTimeout(() => {
      if (
        this.showOmsStoresDropdown &&
        appIntegrationAccount?.app !== EAppAccountType.TRACKSTAR
      ) {
        this.pullOrderOmsStoreIdControl?.setValidators(Validators.required);
      } else {
        this.form?.get('pullOrderOmsStoreId')?.clearValidators();
      }
      this.pullOrderOmsStoreIdControl?.updateValueAndValidity();
    });
  }

  onPushOrderAppAccountSelected(
    appIntegrationAccount: Partial<IAppIntegrationAccount>,
  ) {
    this.selectedPushOrderAppIntegrationAccount = appIntegrationAccount;

    setTimeout(() => {
      if (
        this.showPushOrderOmsStoresDropdown &&
        this.selectedPushOrderAppIntegrationAccount?.app ===
          EAppAccountType.SHIPSTATION
      ) {
        this.pushOrderOmsStoreIdControl?.setValidators(Validators.required);
        this.pushOrderOmsStoreIdControl?.updateValueAndValidity();
      } else {
        this.pushOrderOmsStoreIdControl?.clearValidators();
      }
      this.pushOrderOmsStoreIdControl?.updateValueAndValidity();

      if (this.shouldSetShppingMethod) {
        this.omsShippingMethodIdControl?.setValidators(Validators.required);
        this.omsShippingMethodIdControl?.updateValueAndValidity();
      } else {
        this.omsShippingMethodIdControl?.clearValidators();
        this.omsShippingMethodIdControl?.updateValueAndValidity();
      }

      this.form.updateValueAndValidity();
    });
  }

  onAppAccountSelect(data: Partial<IAppIntegrationAccount>): void {
    // Check if the data object and its credentials property exist
    if (data && data?.credentials) {
      // Set the warehouse_id in formData with the value from data.credentials.warehouse_id
      const warehouseId =
        this.currentChannel?.credentials?.warehouse_id ??
        data?.credentials?.warehouse_id;
      this.form?.get('credentials')?.get('warehouse_id')?.setValue(warehouseId);
    }
  }

  onTogglePullOrders(event: MatSlideToggleChange): void {
    if (event.checked) {
      // Add form for pull orders
      // TODO: Create form group each block to easier manage form state like setValidators
      this.pullOrderAppIntegrationAccountIdControl?.setValidators(
        Validators.required,
      );
    } else {
      // Remove required validators
      // TODO: Create form group each block to easier manage form state like clearValidators
      this.pullOrderAppIntegrationAccountIdControl?.clearValidators();
      this.pullOrderOmsStoreIdControl?.clearValidators();

      this.pullOrderAppIntegrationAccountIdControl?.setValue(null);
      this.pullOrderOmsStoreIdControl?.setValue(null);

      this.pullOrderAutoCreateInvoiceEnabledControl.setValue(false);
      this.invoiceTypeIdControl.clearValidators();
      this.invoiceTypeIdControl?.setValue(null);
      this.invoiceTypeLabelControl?.setValue('');

      this.invoiceCreateTimingControl.clearValidators();
      this.invoiceCreateTimingControl?.setValue(null);

      const saveContactToCrmFromIncomingOrdersControl = this.form?.get('saveContactToCrmFromIncomingOrders') as UntypedFormControl;
      saveContactToCrmFromIncomingOrdersControl?.setValue(false);
    }

    setTimeout(() => {
      this.form.updateValueAndValidity();
    });
  }

  onToggleAutoInvoice(event: MatSlideToggleChange): void {
    if (event.checked) {
      this.invoiceTypeIdControl.setValidators([Validators.required]);
      this.invoiceCreateTimingControl.setValidators([Validators.required]);
    } else {
      this.invoiceTypeIdControl.clearValidators();
      this.invoiceTypeIdControl?.setValue(null);

      this.invoiceCreateTimingControl.clearValidators();
      this.invoiceCreateTimingControl?.setValue(null);

      this.invoiceTypeLabelControl?.setValue('');
    }

    this.invoiceTypeIdControl.updateValueAndValidity();
    this.invoiceCreateTimingControl.updateValueAndValidity();

    this.form.updateValueAndValidity();
  }

  onTogglePushOrders(event: MatSlideToggleChange): void {
    if (event.checked) {
      // Add form for pull orders
      // TODO: Create form group each block to easier manage form state like setValidators
      this.pushOrderAppIntegrationAccountIdControl?.setValidators(
        Validators.required,
      );
      this.pushOrderOmsStoreIdControl?.setValidators(Validators.required);
    } else {
      // Remove required validators
      // TODO: Create form group each block to easier manage form state like clearValidators
      this.pushOrderAppIntegrationAccountIdControl?.clearValidators();
      this.pushOrderOmsStoreIdControl?.clearValidators();
      this.omsShippingMethodIdControl?.clearValidators();

      this.pushOrderAppIntegrationAccountIdControl?.setValue(null);
      this.pushOrderOmsStoreIdControl?.setValue(null);
      this.omsShippingMethodIdControl?.setValue(null);
      this.autoPushOrderAfterMinutesControl?.setValue(null);
      this.autoPushFulfillmentUpdatesToOrderSourceControl?.setValue(null);
    }

    // Update the shouldShowAutoPushOrderField value
    this.updateShouldShowAutoPushOrderField();

    setTimeout(() => {
      this.form.updateValueAndValidity();
    });
  }

  onTogglePushStocks(event: MatSlideToggleChange): void {
    if (event.checked && this.pushStocksToOmsEnabled) {
      // Add form for pull orders
      // TODO: Create form group each block to easier manage form state like setValidators
      if (
        (!this.isNonSalesChannel || this.isFulfillmentChannel) &&
        this.ftChannelTypeEnabled
      ) {
        this.pushStocksAppIntegrationAccountIdControl?.clearValidators();
        this.pushStocksAppIntegrationAccountIdControl?.setValue(null);
      } else {
        this.pushStocksAppIntegrationAccountIdControl?.setValidators(
          Validators.required,
        );
      }
      this.form?.get('pushStocksToOmsOptions').patchValue({
        includeIncomingStocksAsSellable: false,
        pushIncomingStocksFromSupplierPurchaseOrderEnabled: false,
        pushUnitCost: false,
      });
    } else {
      // Remove required validators
      // TODO: Create form group each block to easier manage form state like clearValidators
      this.pushStocksAppIntegrationAccountIdControl?.clearValidators();
      this.pushStocksAppIntegrationAccountIdControl?.setValue(null);

      this.pushStocksInventoryLocationIdControl?.setValue(null);
    }
    this.form.updateValueAndValidity();
  }

  onSetInvoiceType(invoiceType: IInvoiceType): void {
    this.invoiceTypeIdControl.setValue(invoiceType?.id);
  }

  onSubmit(): void {
    let payload = this.form.value;

    // For sales channel, ensure autoPushFulfillmentUpdatesToOrderSource is in the correct place
    // and pushStocksAppIntegrationAccount is null
    if (!this.isNonSalesChannel && this.ftChannelTypeEnabled) {
      payload = {
        ...payload,
        pullOrdersFromOmsOptions: {
          ...payload.pullOrdersFromOmsOptions,
          autoPushFulfillmentUpdatesToOrderSource:
            payload.pullOrdersFromOmsOptions
              .autoPushFulfillmentUpdatesToOrderSource,
        },
        pushStocksAppIntegrationAccountId: null,
      };

      delete payload.pushOrdersToOmsOptions.autoPushFulfillmentUpdatesToOrderSource;
    }

    payload = {
      ...payload,
      pullOrdersFromOmsOptions: {
        ...payload.pullOrdersFromOmsOptions,
        incomingCustomFieldMapping:
          this.incomingCustomFieldMappingFormArray.controls
            .map((control) => control.value)
            .filter((value) => value.incomingField && value.lumField),
      },
      pushOrdersToOmsOptions: {
        ...payload.pushOrdersToOmsOptions,
        pushOrderCustomFieldMapping:
          this.pushOrderCustomFieldMappingFormArray.controls
            .map((control) => control.value)
            .filter((value) => value.lumField && value.pushField),
        pushCustomFields: this.pushCustomFieldsControl.controls
          .map((control) => control.value)
          .filter((value) => value.key && value.value),
      },
    };

    if (!payload.pullOrdersFromOms) {
      delete payload.pullOrderAppIntegrationAccountId;
      delete payload.pullOrderOmsStoreId;
      delete payload.filters;
    } else if (
      !this.selectedAppHasStores(this.selectedPullAppIntegrationAccount?.app || '')
    ) {
      delete payload.pullOrderOmsStoreId;
    }

    delete payload.invoiceTypeLabel;

    if (this.currentChannel) {
      const ftChannelTypes = this.featureToggleService.isActive(
        'red_DV-3395_channel_types',
      );
      if (ftChannelTypes) {
        const changesToFulfillmentOrderPrioListSettings = [];
        const hasChangesToWarehouseGroup =
          this.currentChannel.warehouseGroupId !== payload.warehouseGroupId &&
          !(
            this.currentChannel.warehouseGroupId === null &&
            payload.warehouseGroupId === null
          ) &&
          !(this.currentChannel.warehouseGroupId === payload.warehouseGroupId);
        if (hasChangesToWarehouseGroup) {
          changesToFulfillmentOrderPrioListSettings.push('Warehouse Group');
        }

        const currentAppIntegrationAccountId =
          this.currentChannel.pushOrderIntegrationMapping
            ?.appIntegrationAccountId ?? null;
        const payloadAppIntegrationAccountId =
          payload.pushOrderAppIntegrationAccountId ?? null;
        const hasChangesToPushOrderAppIntegrationAccount =
          currentAppIntegrationAccountId !== payloadAppIntegrationAccountId &&
          !(
            currentAppIntegrationAccountId == null &&
            payloadAppIntegrationAccountId == null
          ) &&
          !(currentAppIntegrationAccountId === payloadAppIntegrationAccountId);
        if (hasChangesToPushOrderAppIntegrationAccount) {
          changesToFulfillmentOrderPrioListSettings.push(
            'Destination App Integration Account',
          );
        }

        const currentOmsStoreId =
          this.currentChannel.pushOrderIntegrationMapping?.omsStoreId ?? null;
        const payloadOmsStoreId = payload.pushOrderOmsStoreId ?? null;
        const hasChangesToPushOrderStore =
          currentOmsStoreId !== payloadOmsStoreId &&
          !(currentOmsStoreId == null && payloadOmsStoreId == null) &&
          !(currentOmsStoreId === payloadOmsStoreId);
        if (hasChangesToPushOrderStore) {
          changesToFulfillmentOrderPrioListSettings.push('Destination Store');
        }

        if (changesToFulfillmentOrderPrioListSettings.length > 0) {
          const changesMessage = `Changes detected in the following settings (${changesToFulfillmentOrderPrioListSettings.join(', ')}). Updating channel will also update the fulfillment priority list lines associated with this channel.`;

          this.dialog
            .open(UiConfirmationDialogComponent, {
              data: {
                title: 'Update Channel?',
                message: changesMessage,
              },
              width: DialogConfig.sizes.small,
            })
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((confirm: boolean) => {
              if (confirm) {
                this.updateChannel(payload);
              }

              return;
            });

          return;
        }
      }

      this.updateChannel(payload);
    } else {
      this.createChannel({
        ...this.currentChannel,
        ...payload,
      });
    }
  }

  private getOutgoingCustomFieldsMapping(): void {
    this.manageApiService
      .getCustomFieldsMapping()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((fields: IFieldLabel[]) => (this.outgoingCustomFields = fields));
  }

  private createChannel(payload: any): void {
    this.channelFacadeService
      .create(payload)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.form.reset();
          this.router.navigate(['/channels/v2/list']);
          this.snackBar.open('Channel created successfully', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
            panelClass: 'success',
          });
        },
        error: (response) => {
          this.snackBar.open(
            response?.error || 'Failed to create channel.',
            'Close',
            {
              panelClass: 'error',
            },
          );
        },
      });
  }

  private updateChannel(payload: any): void {
    this.channelFacadeService
      .update({
        id: this.currentChannel?.id,
        data: payload,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.router.navigate(['/channels/v2/list']);
          this.snackBar.open('Channel updated successfully', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
            panelClass: 'success',
          });
        },
        error: (error) => {
          this.snackBar.open(
            error?.message || 'Failed to update channel.',
            'Close',
            {
              panelClass: 'error',
            },
          );
        },
      });
  }

  private initCurrentChannel(): void {
    const currentChannelId = this.route.snapshot.params.id;
    if (currentChannelId) {
      const params: { [param: string]: string | string[] } = {
        relations: [
          'pullOrderIntegrationMapping',
          'pullOrderIntegrationMapping.invoiceType',
          'pushOrderIntegrationMapping',
          'tags',
          'getProductsIntegrationMapping',
          'pushStockIntegrationMapping',
          'metafields',
        ].join(','),
      };

      this.channelFacadeService
        .getById({
          id: Number(currentChannelId),
          params,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: () => {
            this.initForm();
          },
          error: (error) => {
            this.snackBar.open(
              error?.message || 'Failed to fetch channel.',
              'Close',
              {
                panelClass: 'error',
              },
            );
          },
        });
    }
  }

  private initFeatureFlags(): void {
    this.featureFlags = this.route.snapshot.data['featureFlags'];
    this.ftChannelTypeEnabled = this.featureFlagUtilService.isFeatureEnabled(
      'red_DV-3395_channel_types',
    );
  }

  private initForm(): void {
    const customFields =
      this.currentChannel?.pushOrderIntegrationMapping?.options?.pushCustomFields ||
      [];

    this.form = this.fb.group({
      label: [this.currentChannel?.label || null, Validators.required],
      tagIds: this.currentChannel?.tags?.length
        ? this.fb.array(this.currentChannel.tags.map((tag) => tag.id))
        : this.fb.array([]),
      description: [this.currentChannel?.description || null],
      pullOrdersFromOms: [
        this.currentChannel?.pullOrderIntegrationMapping ? true : false,
      ],
      channelType: [
        this.currentChannel?.channelType || EChannelType.END_TO_END_CHANNEL,
        this.ftChannelTypeEnabled ? [Validators.required] : [],
      ],

      // ORDER SOURCE
      // TODO: Create form group each block to easier manage form state
      pullOrderAppIntegrationAccountId: [
        this.currentChannel?.pullOrderIntegrationMapping?.appIntegrationAccountId ||
          null,
      ],
      pullOrderOmsStoreId: [
        this.currentChannel?.pullOrderIntegrationMapping?.omsStoreId || null,
      ],
      // PULL ORDERS OPTIONS
      pullOrdersFromOmsOptions: this.fb.group({
        incomingCustomFieldMapping: this.fb.array([]),
        // For channel type: Sales Channel
        autoPushFulfillmentUpdatesToOrderSource: [
          this.currentChannel?.pullOrderIntegrationMapping?.options
            ?.autoPushFulfillmentUpdatesToOrderSource ?? true,
        ],
        saveContactToCrmFromIncomingOrders: [
          (() => {
            const value = this.currentChannel?.pullOrderIntegrationMapping?.options?.saveContactToCrmFromIncomingOrders;
            const isEmpty = value === undefined || value === null;
            return isEmpty ? (!this.currentChannel) : value;
          })(),
        ],
      }),
      invoiceTypeLabel: [this.currentChannel?.invoiceType?.label || ''],
      filters: this.currentChannel?.filters?.length
        ? this.fb.array(
            this.currentChannel.filters.map((filter) =>
              this.fb.group({
                key: [filter.key, Validators.required],
                condition: [filter.condition, Validators.required],
                value: [filter.value, Validators.required],
              }),
            ),
          )
        : this.fb.array([]),

      // ORDER DESTINATION
      // TODO: Create form group each block to easier manage form state
      warehouseGroupId: [
        this.currentChannel?.warehouseGroupId || null,
        Validators.required,
      ],
      pushOrdersToOms: [
        this.currentChannel?.pushOrderIntegrationMapping ? true : false,
      ],
      pushOrderAppIntegrationAccountId: [
        this.currentChannel?.pushOrderIntegrationMapping?.appIntegrationAccountId,
      ],
      pushOrderOmsStoreId: [
        this.currentChannel?.pushOrderIntegrationMapping?.omsStoreId,
      ],
      // For sales channel
      autoPushFulfillmentUpdatesToOrderSource: [
        this.currentChannel?.pushOrderIntegrationMapping?.options
          ?.autoPushFulfillmentUpdatesToOrderSource ?? true,
      ],

      // PUSH STOCKS
      // TODO: Create form group each block to easier manage form state
      pushOrdersToOmsOptions: this.fb.group({
        omsShippingMethodId: [
          this.currentChannel?.pushOrderIntegrationMapping?.options
            ?.omsShippingMethodId || null,
        ],
        autoPushOrderAfterMinutes: [
          this.currentChannel?.pushOrderIntegrationMapping?.options
            ?.autoPushOrderAfterMinutes || null,
        ],
        // For non-sales channels
        autoPushFulfillmentUpdatesToOrderSource: [
          this.currentChannel?.pushOrderIntegrationMapping?.options
            ?.autoPushFulfillmentUpdatesToOrderSource ?? true,
        ],
        pushCustomFields: this.fb.array(
          customFields?.map((field) =>
            this.fb.group({
              key: [field.key],
              value: [field.value],
            }),
          ) || [],
        ),
        pushOrderCustomFieldMapping: this.fb.array([]),
      }),
      pushStocksToOms: [
        this.currentChannel?.pushStockIntegrationMapping ? true : false,
      ],
      pushStocksAppIntegrationAccountId: [
        this.currentChannel?.pushStockIntegrationMapping?.appIntegrationAccountId,
      ],
      pushStocksInventoryLocationId: [
        this.currentChannel?.pushStockIntegrationMapping?.inventoryLocationId ||
          null,
      ],
      pushStocksToOmsOptions: this.fb.group({
        includeIncomingStocksAsSellable: [
          this.currentChannel?.pushStockIntegrationMapping?.options
            ?.includeIncomingStocksAsSellable || false,
        ],
        pushIncomingStocksFromSupplierPurchaseOrderEnabled: [
          this.currentChannel?.pushStockIntegrationMapping?.options
            ?.pushIncomingStocksFromSupplierPurchaseOrderEnabled || false,
        ],
        pushUnitCost: [
          this.currentChannel?.pushStockIntegrationMapping?.options?.pushUnitCost ||
            false,
        ],
      }),
      isPOSChannel: [this.currentChannel?.isPOSChannel || false],
      autoCreateInvoiceEnabled: [
        this.channelMetafields?.[
          EChannelMetaKey.CREATE_INVOICE_AUTOMATICALLY_FOR_AN_ORDER
        ] || false,
      ],
      autoSyncEdiEnabled: [
        this.channelMetafields?.[EChannelMetaKey.AUTO_SYNC_EDI] || false,
      ],
      invoiceTypeId: [
        this.channelMetafields?.[
          EChannelMetaKey.CREATE_INVOICE_DEFAULT_INVOICE_TYPE_ID
        ] || null,
        this.isAutoCreateInvoiceEnabled ? [Validators.required] : [],
      ],
      invoiceCreateTiming: [
        this.isFulfillmentChannel
          ? null
          : this.channelMetafields?.[
              EChannelMetaKey.CREATE_INVOICE_CREATION_TIMING
            ] || EInvoiceCreateTiming.ORDER_RECEIVED,
        this.isFulfillmentChannel ? [] : [Validators.required],
      ],
    });

    if (this.isAutoCreateInvoiceEnabled) {
      this.invoiceTypeIdControl.setValidators([Validators.required]);
      this.pullOrderAppIntegrationAccountIdControl.updateValueAndValidity();
    } else {
      this.invoiceTypeIdControl.clearValidators();
    }

    // Update form values after initialization
    if (this.currentChannel) {
      // Set channel type flags before form initialization
      const channelType =
        this.currentChannel?.channelType || EChannelType.END_TO_END_CHANNEL;
      this.isFulfillmentChannel = channelType === EChannelType.FULFILLMENT_CHANNEL;
      this.isNonSalesChannel = channelType !== EChannelType.SALES_CHANNEL;

      // Update required validators based on channel type
      if (!this.isNonSalesChannel) {
        this.form.get('warehouseGroupId')?.clearValidators();
      }

      // Set the autoPushFulfillmentUpdatesToOrderSource value from channel data
      if (this.currentChannel.pullOrderIntegrationMapping?.options) {
        const options = this.currentChannel.pullOrderIntegrationMapping.options;

        // For non-sales channels, set in pushOrdersToOmsOptions
        if (!this.isNonSalesChannel) {
          this.form.get('pushOrdersToOmsOptions')?.patchValue({
            autoPushFulfillmentUpdatesToOrderSource:
              options.autoPushFulfillmentUpdatesToOrderSource || false,
          });
        }
      }

      // Update form arrays for custom field mappings
      this.currentChannel?.pullOrderIntegrationMapping?.options?.incomingCustomFieldMapping.forEach(
        (field) => {
          this.onAddIncomingCustomField(
            this.fb.group({
              incomingField: [field.incomingField],
              lumField: [field.lumField],
            }),
          );
        },
      );

      this.currentChannel?.pushOrderIntegrationMapping?.options?.pushOrderCustomFieldMapping.forEach(
        (field) => {
          this.onAddPushOrderCustomField(
            this.fb.group({
              lumField: [field.lumField],
              pushField: [field.pushField],
            }),
          );
        },
      );

      // Update the shouldShowAutoPushOrderField value
      this.updateShouldShowAutoPushOrderField();
    }

    if (this.ftChannelTypeEnabled) {
      // Set initial values based on channel type
      const channelType = this.form.get('channelType')?.value;
      this.isFulfillmentChannel = channelType === EChannelType.FULFILLMENT_CHANNEL;
      this.isNonSalesChannel = channelType !== EChannelType.SALES_CHANNEL;

      // Move the value to the correct location based on channel type
      if (!this.isNonSalesChannel) {
        const value = this.form.get(
          'pushOrdersToOmsOptions.autoPushFulfillmentUpdatesToOrderSource',
        )?.value;
        this.form
          .get('pullOrdersFromOmsOptions.autoPushFulfillmentUpdatesToOrderSource')
          ?.setValue(value);
        this.form
          .get('pushOrdersToOmsOptions.autoPushFulfillmentUpdatesToOrderSource')
          ?.setValue(null);
      }
    }

    this.invoiceTypeIdControl.updateValueAndValidity();
  }

  onTogglePosChannel(event: MatCheckboxChange): void {
    if (event.checked) {
      this.pullOrderAppIntegrationAccountIdControl?.clearValidators();
      this.pullOrderAppIntegrationAccountIdControl?.setValue(null);
      this.pullOrdersFromOmsControl?.clearValidators();
      this.pullOrdersFromOmsControl?.setValue(false);
      this.pullOrderOmsStoreIdControl?.clearValidators();
      this.pullOrderOmsStoreIdControl?.setValue(null);
    } else {
      if (this.pullOrdersFromOmsControl?.value) {
        this.pullOrderAppIntegrationAccountIdControl?.setValidators(
          Validators.required,
        );
      } else {
        this.pullOrderAppIntegrationAccountIdControl?.clearValidators();
        this.pullOrderAppIntegrationAccountIdControl?.setValue(null);
      }

      if (this.showOmsStoresDropdown) {
        this.pullOrderOmsStoreIdControl?.setValidators(Validators.required);
      } else {
        this.pullOrderOmsStoreIdControl?.clearValidators();
        this.pullOrderOmsStoreIdControl?.setValue(null);
      }
    }

    this.form.updateValueAndValidity();
  }

  onAddIncomingCustomField(group?: FormGroup): void {
    const newGroup =
      group ||
      this.fb.group({
        incomingField: [''],
        lumField: [''],
      });

    // Flag to prevent infinite loop
    let updating = false;

    const handleValueChange = () => {
      if (updating) return; // Exit if already updating to avoid infinite loop

      updating = true;
      this.updateValidators({
        sourceField: newGroup.get('incomingField'),
        targetField: newGroup.get('lumField'),
      });
      updating = false;
    };

    // Subscribe to changes in both fields
    newGroup
      .get('incomingField')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(handleValueChange);
    newGroup
      .get('lumField')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(handleValueChange);

    this.incomingCustomFieldMappingFormArray.push(newGroup);

    // Update form validation
    this.form.updateValueAndValidity();
  }

  onRemoveIncomingCustomFieldMapping(index: number): void {
    this.incomingCustomFieldMappingFormArray.removeAt(index);
  }

  onAddPushOrderCustomField(group?: FormGroup): void {
    const newGroup =
      group ||
      this.fb.group({
        lumField: [''],
        pushField: [''],
      });

    // Flag to prevent infinite loop
    let updating = false;

    const handleValueChange = () => {
      if (updating) return; // Exit if already updating to avoid infinite loop

      updating = true;
      this.updateValidators({
        sourceField: newGroup.get('lumField'),
        targetField: newGroup.get('pushField'),
      });
      updating = false;
    };

    // Subscribe to changes in both fields
    newGroup
      .get('lumField')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(handleValueChange);
    newGroup
      .get('pushField')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(handleValueChange);

    this.pushOrderCustomFieldMappingFormArray.push(newGroup);
    // Update form validation
    this.form.updateValueAndValidity();
  }

  onRemovePushCustomFieldMapping(index: number): void {
    this.pushOrderCustomFieldMappingFormArray.removeAt(index);
  }

  private updateValidators(data: {
    sourceField: AbstractControl;
    targetField: AbstractControl;
  }): void {
    const { sourceField, targetField } = data;

    if (sourceField.value.trim() !== '' || targetField.value.trim() !== '') {
      sourceField.setValidators([Validators.required]);
      targetField.setValidators([Validators.required]);
    } else {
      sourceField.clearValidators();
      targetField.clearValidators();
    }

    sourceField.updateValueAndValidity({ emitEvent: false });
    targetField.updateValueAndValidity({ emitEvent: false });
  }

  onChannelTypeChange(event: any): void {
    // Set visibility flags based on channel type
    this.isFulfillmentChannel = event.value === EChannelType.FULFILLMENT_CHANNEL;
    this.isNonSalesChannel = event.value !== EChannelType.SALES_CHANNEL;

    // Update warehouseGroupId validation based on channel type
    if (this.isNonSalesChannel) {
      this.warehouseGroupIdControl?.setValidators([Validators.required]);
      // Move autoPushFulfillmentUpdatesToOrderSource to pushOrdersToOmsOptions
      const value = this.form.get(
        'pullOrdersFromOmsOptions.autoPushFulfillmentUpdatesToOrderSource',
      )?.value;
      this.form
        .get('pushOrdersToOmsOptions.autoPushFulfillmentUpdatesToOrderSource')
        ?.setValue(value);
      this.form
        .get('pullOrdersFromOmsOptions.autoPushFulfillmentUpdatesToOrderSource')
        ?.setValue(!this.currentChannel ? true : null);
    } else {
      this.warehouseGroupIdControl?.clearValidators();
      this.warehouseGroupIdControl?.setValue(null);
      // Move autoPushFulfillmentUpdatesToOrderSource to pullOrdersFromOmsOptions
      const value = this.form.get(
        'pushOrdersToOmsOptions.autoPushFulfillmentUpdatesToOrderSource',
      )?.value;
      this.form
        .get('pullOrdersFromOmsOptions.autoPushFulfillmentUpdatesToOrderSource')
        ?.setValue(value);
      this.form
        .get('pushOrdersToOmsOptions.autoPushFulfillmentUpdatesToOrderSource')
        ?.setValue(!this.currentChannel ? true : null);
    }

    // Reset push stocks validation when changing channel type
    if (this.pushStocksToOmsEnabled) {
      if (event.value === EChannelType.END_TO_END_CHANNEL) {
        this.pushStocksAppIntegrationAccountIdControl?.setValidators([
          Validators.required,
        ]);
      } else {
        this.pushStocksAppIntegrationAccountIdControl?.clearValidators();
      }
      this.pushStocksAppIntegrationAccountIdControl?.updateValueAndValidity();
    }

    this.updateShouldShowAutoPushOrderField();
    this.warehouseGroupIdControl?.updateValueAndValidity();
    this.form.updateValueAndValidity();
  }

  private updateShouldShowAutoPushOrderField(): void {
    this.shouldShowAutoPushOrderField =
      this.shouldPushOrders &&
      (!this.ftChannelTypeEnabled || this.isNonSalesChannel);
  }

  private selectedAppHasStores(app: string): boolean {
    if (app.length === 0) {
      return false;
    }
    const withStoresOms: string[] = [
      EAppAccountType.SHIPKEY,
      EAppAccountType.SHIPSTATION,
      EAppAccountType.TRACKSTAR,
    ];

    return withStoresOms.includes(app);
  }

  get incomingCustomFieldMappingFormArray(): UntypedFormArray {
    return this.form.get(
      'pullOrdersFromOmsOptions.incomingCustomFieldMapping',
    ) as UntypedFormArray;
  }

  get pushOrderCustomFieldMappingFormArray(): UntypedFormArray {
    return this.form.get(
      'pushOrdersToOmsOptions.pushOrderCustomFieldMapping',
    ) as UntypedFormArray;
  }

  get tagIds(): UntypedFormArray {
    return this.form?.get('tagIds') as UntypedFormArray;
  }

  get selectedPullAppIntegrationAccountId(): number {
    return parseInt(this.form.value.pullOrderAppIntegrationAccountId);
  }

  get shouldPullOrders(): boolean {
    return this.form.value.pullOrdersFromOms;
  }

  get ftEdiIntegrationEnabled(): boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      EFeatureFlag.EDI_INTEGRATION,
    );
  }

  get showPushOrders(): boolean {
    return (
      this.ftEdiIntegrationEnabled &&
      this.selectedPullAppIntegrationAccount?.app === EAppAccountType.EDI
    );
  }

  get showOmsStoresDropdown(): boolean {
    return (
      this.selectedPullAppIntegrationAccountId > 0 &&
      this.selectedPullAppIntegrationAccount &&
      this.selectedAppHasStores(this.selectedPullAppIntegrationAccount?.app || '')
    );
  }

  get ediWebhookUrl(): string {
    if (this.selectedPullAppIntegrationAccount?.app === EAppAccountType.EDI) {
      if (this.currentChannel?.id && this.currentChannel.uuid) {
        return `${this.environment.url}/api/edi/${this.currentChannel.uuid}`;
      }

      return 'Webhook url will be generated after this channel will be created.';
    }

    return '';
  }

  get showChannelFilters(): boolean {
    return (
      this.selectedPullAppIntegrationAccount?.app &&
      this.selectedPullAppIntegrationAccountId > 0
    );
  }

  // ORDER DESTINATION GETTERS

  get ediPushOrderToOmsAppTypes(): EAppAccountType[] {
    const suppotedAppTypes = [
      EAppAccountType.EDI,
      EAppAccountType.SHIPSTATION,
      EAppAccountType.TRACKSTAR,
    ];

    return suppotedAppTypes;
  }

  get shouldPushOrders(): boolean {
    return this.form.value.pushOrdersToOms;
  }

  get selectedPushOrderAppIntegrationAccountId(): number {
    return parseInt(this.form.value.pushOrderAppIntegrationAccountId);
  }

  get showPushOrderOmsStoresDropdown(): boolean {
    return (
      this.selectedPushOrderAppIntegrationAccountId > 0 &&
      this.selectedPushOrderAppIntegrationAccount &&
      this.selectedAppHasStores(
        this.selectedPushOrderAppIntegrationAccount.app || '',
      )
    );
  }

  get pushOrdersToOmsOptionsGroup(): UntypedFormGroup {
    return this.form?.get('pushOrdersToOmsOptions') as UntypedFormGroup;
  }

  // PUSH STOCKS GETTERS
  get showPushStocksToggle(): boolean {
    return this.showPushOrders && this.form?.get('pushOrdersToOms')?.value;
  }

  /**
   * The required fields for tracktar aren't always consistent, this doesn't usually work
   */
  get shouldSetShppingMethod(): boolean {
    return (
      this.shouldPushOrders &&
      false &&
      this.selectedPushOrderAppIntegrationAccount?.app === EAppAccountType.TRACKSTAR
    );
  }

  get pushStocksToOmsEnabled(): boolean {
    return this.form?.get('pushStocksToOms')?.value;
  }

  get pullOrderAppIntegrationAccountIdControl(): UntypedFormControl {
    return this.form?.get('pullOrderAppIntegrationAccountId') as UntypedFormControl;
  }

  get warehouseGroupIdControl(): UntypedFormControl {
    return this.form?.get('warehouseGroupId') as UntypedFormControl;
  }

  get pullOrderOmsStoreIdControl(): UntypedFormControl {
    return this.form?.get('pullOrderOmsStoreId') as UntypedFormControl;
  }

  get pullOrdersFromOmsControl(): UntypedFormControl {
    return this.form?.get('pullOrdersFromOms') as UntypedFormControl;
  }

  get pushOrderAppIntegrationAccountIdControl(): UntypedFormControl {
    return this.form?.get('pushOrderAppIntegrationAccountId') as UntypedFormControl;
  }

  get pushOrderOmsStoreIdControl(): UntypedFormControl {
    return this.form?.get('pushOrderOmsStoreId') as UntypedFormControl;
  }

  get omsShippingMethodIdControl(): UntypedFormControl {
    return this.pushOrdersToOmsOptionsGroup.get(
      'omsShippingMethodId',
    ) as UntypedFormControl;
  }

  get autoPushOrderAfterMinutesControl(): UntypedFormControl {
    return this.pushOrdersToOmsOptionsGroup.get(
      'autoPushOrderAfterMinutes',
    ) as UntypedFormControl;
  }

  get autoPushFulfillmentUpdatesToOrderSourceControl(): UntypedFormControl {
    return this.pushOrdersToOmsOptionsGroup.get(
      'autoPushFulfillmentUpdatesToOrderSource',
    ) as UntypedFormControl;
  }

  get selectedPushStocksAppIntegrationAccountId(): number {
    return parseInt(this.form.value.pushStocksAppIntegrationAccountId);
  }

  get pushStocksAppIntegrationAccountIdControl(): UntypedFormControl {
    return this.form?.get(
      'pushStocksAppIntegrationAccountId',
    ) as UntypedFormControl;
  }

  get pushStocksInventoryLocationIdControl(): UntypedFormControl {
    return this.form?.get('pushStocksInventoryLocationId') as UntypedFormControl;
  }

  get isPosChannelControl(): UntypedFormControl {
    return this.form?.get('isPOSChannel') as UntypedFormControl;
  }

  get posChannelEnabled(): boolean {
    return this.isPosChannelControl.value;
  }

  // STATES GETTERS
  get loading(): boolean {
    return this.channelFacadeService.channelState.loading;
  }

  get loading$(): Observable<boolean> {
    return this.channelFacadeService.loading$;
  }

  get submitting$(): Observable<boolean> {
    return this.channelFacadeService.submitting$;
  }

  get currentChannel(): IChannel | null {
    return this.channelFacadeService.channelState.currentChannel;
  }

  get pullOrderAutoCreateInvoiceEnabledControl(): UntypedFormControl {
    return this.form?.get('autoCreateInvoiceEnabled') as UntypedFormControl;
  }

  get isAutoCreateInvoiceEnabled(): boolean {
    return this.form?.get('autoCreateInvoiceEnabled')?.value || false;
  }

  get invoiceTypeIdControl(): UntypedFormControl {
    return this.form?.get('invoiceTypeId') as UntypedFormControl;
  }

  get invoiceCreateTimingControl(): UntypedFormControl {
    return this.form?.get('invoiceCreateTiming') as UntypedFormControl;
  }

  get invoiceTypeLabelControl(): UntypedFormControl {
    return this.form?.get('invoiceTypeLabel') as UntypedFormControl;
  }

  get pushCustomFieldsControl(): UntypedFormArray {
    return this.form?.get(
      'pushOrdersToOmsOptions.pushCustomFields',
    ) as UntypedFormArray;
  }

  get channelMetafields(): { [key: string]: string } | null {
    return (
      this.currentChannel?.metafields?.reduce((acc, metafield) => {
        acc[metafield.metaKey] = metafield.metaValue;

        return acc;
      }, {}) || null
    );
  }
}
